import React, { Fragment, useState } from 'react';
import { css } from 'twin.macro';
import { BootstrapToolTip } from '@/_elements/ToolTip';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Button, Form } from 'react-bootstrap';
import { BsTrash, BsGripVertical } from 'react-icons/bs';
import { customCheckbox } from '@/_elements/CustomCheckbox';
const cleanUpValue = (value) => {
    return String(value || '')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/[^a-zA-Z0-9\-!@#$%^&*()_+\[\]{}|:;<>? ]/g, '');
};

const DraggableOptions = React.memo(
    ({ element, register, options = [], setOptions, handleRemoveOption, handleNewOption }) => {
        const [enableDataValue, setEnableDataValue] = useState((element?.options || {}).enableDataValue || false);
        const isMedicationSelect = (element?.specialityKey || (element?.config || {})?.specialityKey) === 'medication';

        const handleDragEnd = (result) => {
            if (!result.destination) return;
            const reorderedOptions = Array.from(options);
            const [movedOption] = reorderedOptions.splice(result.source.index, 1);
            reorderedOptions.splice(result.destination.index, 0, movedOption);
            setOptions(reorderedOptions);
        };

        const handleLabelEdit = (e, optionId) => {
            const newValue = e.target.value;
            setOptions((prevOptions) =>
                prevOptions.map((option) => (option.value === optionId ? { ...option, text: newValue } : option))
            );
        };

        const handleDataValueEdit = (e, optionId) => {
            const newValue = (e.target.value || '').trim();

            setOptions((prevOptions) =>
                prevOptions.map((option) => (option.value === optionId ? { ...option, dataValue: newValue } : option))
            );
        };

        const handleDosageEdit = (e, optionId) => {
            const newValue = e.target.value;
            setOptions((prevOptions) =>
                prevOptions.map((option) => (option.value === optionId ? { ...option, dosage: newValue } : option))
            );
        };

        const handleFrequencyEdit = (e, optionId) => {
            const newValue = e.target.value;
            setOptions((prevOptions) =>
                prevOptions.map((option) => (option.value === optionId ? { ...option, frequency: newValue } : option))
            );
        };

        const toggleDataValue = (isChecked) => {
            setEnableDataValue(isChecked);
            if (isChecked) {
                setOptions(
                    options.map((option) => ({
                        ...option,
                        dataValue: option?.dataValue || '',
                    }))
                );
            }
        };

        return (
            <React.Fragment>
                <div
                    className="d-flex flex-row justify-content-between"
                    css={css`
                        padding: 8px;
                        margin-top: 46px;
                    `}
                >
                    <div>
                        <strong
                            css={css`
                                font-size: 18px;
                            `}
                        >
                            Options:
                        </strong>
                    </div>
                    <div>
                        <Form.Group
                            controlId="enableDataValue"
                            css={css`
                                display: flex;
                                align-items: center;
                                .requiredCheckbox {
                                    width: 20px;
                                    height: 20px;
                                }
                            `}
                        >
                            <Form.Control
                                className="requiredCheckbox"
                                type="checkbox"
                                css={customCheckbox}
                                name="options.enableDataValue"
                                checked={enableDataValue}
                                onChange={(e) => {
                                    toggleDataValue(e.target.checked);
                                }}
                                ref={register()}
                            />
                            <Form.Label
                                css={css`
                                    margin: 0;
                                `}
                            >
                                Enable data values
                            </Form.Label>
                        </Form.Group>
                    </div>
                </div>
                {enableDataValue && (
                    <Fragment>
                        <div className="d-flex flex-column border border-1 p-2">
                            <strong className="mb-2">Note:</strong>
                            <ul className="mb-0 ps-3 ml-4">
                                <li>Optional values that can be used for calculations and logic.</li>
                                <li>Leave empty if not needed.</li>
                            </ul>
                        </div>
                        <hr />
                        <div className="d-flex flex-row justify-content-between align-items-center">
                            <div className="text-center w-100">
                                <strong>Label</strong>
                            </div>
                            <div className="text-center w-100">
                                <strong>Data Value</strong>
                            </div>
                        </div>
                    </Fragment>
                )}

                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="options">
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                {options.map((option, index) => (
                                    <Draggable key={option.value} draggableId={option.value} index={index}>
                                        {(provided) => (
                                            <div
                                                className="d-flex mb-2"
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                            >
                                                <div
                                                    {...provided.dragHandleProps}
                                                    css={css`
                                                        cursor: grab;
                                                        display: flex;
                                                        align-items: center;
                                                        justify-content: center;
                                                    `}
                                                >
                                                    <BsGripVertical size={24} />
                                                </div>

                                                <div
                                                    css={css`
                                                        display: flex;
                                                        flex: 1;
                                                        gap: 8px;
                                                    `}
                                                >
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Label"
                                                        value={option.text}
                                                        css={css`
                                                            flex: 2;
                                                        `}
                                                        onChange={(e) => handleLabelEdit(e, option.value)}
                                                    />

                                                    {enableDataValue && (
                                                        <Form.Control
                                                            type="text"
                                                            placeholder="Data value"
                                                            value={option.dataValue || ''}
                                                            css={css`
                                                                flex: 1;
                                                            `}
                                                            onChange={(e) => handleDataValueEdit(e, option.value)}
                                                        />
                                                    )}

                                                    {isMedicationSelect && (
                                                        <>
                                                            <Form.Control
                                                                type="text"
                                                                placeholder="Dosage"
                                                                value={option.dosage || ''}
                                                                css={css`
                                                                    flex: 1;
                                                                `}
                                                                onChange={(e) => handleDosageEdit(e, option.value)}
                                                            />
                                                            <Form.Control
                                                                type="text"
                                                                placeholder="Frequency"
                                                                value={option.frequency || ''}
                                                                css={css`
                                                                    flex: 1;
                                                                `}
                                                                onChange={(e) => handleFrequencyEdit(e, option.value)}
                                                            />
                                                        </>
                                                    )}
                                                </div>

                                                {options.length > 2 && (
                                                    <div
                                                        css={css`
                                                            padding: 2px;
                                                            cursor: pointer;
                                                            margin-left: 8px;
                                                            display: flex;
                                                            align-items: center;
                                                        `}
                                                    >
                                                        <BootstrapToolTip
                                                            text={'Delete'}
                                                            side={'top'}
                                                            cssStyles={
                                                                'top:-70% !important; min-width: fit-content !important'
                                                            }
                                                        >
                                                            <BsTrash
                                                                color="red"
                                                                size="24"
                                                                onClick={() => handleRemoveOption(option.value)}
                                                            />
                                                        </BootstrapToolTip>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>

                <div className="mb-4 ml-4">
                    <Button variant="primary" onClick={handleNewOption}>
                        Add new
                    </Button>
                </div>
            </React.Fragment>
        );
    }
);

export default DraggableOptions;
